import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { API_BASE, authenticatedFetch } from '../utils';
import { usePreferences } from '../contexts/PreferencesContext';
import {
  ACTION_STATUSES,
  normaliseActionStatus,
  useUpdateActionStatus,
} from '../hooks/useActionItemsQueries';
import {
  EMPTY,
  fmtDurationWords,
  fmtInt,
  fmtPercent,
  fmtPhone,
  ratio,
} from '../lib/format';
import { formatInstant } from '../lib/time';
import AudioPlayer from './AudioPlayer';
import { Badge, DirectionBadge, OutcomeBadge, SentimentBadge } from './ui/Badge';
import { Button, IconButton } from './ui/Button';
import { Card, CardHeader } from './ui/Card';
import { SearchInput, SegmentedControl } from './ui/Controls';
import { Target } from './ui/Delta';
import { EmptyState } from './ui/EmptyState';
import { Drawer } from './ui/Modal';
import { Spinner } from './ui/Skeleton';

/**
 * The call detail surface — the most-opened thing in the product.
 *
 * WHY IT IS A DRAWER
 * ------------------
 * Reviewing calls is queue work: you read one, judge it, and move to the next.
 * A centred modal covers the list you are working through, so this is a
 * right-hand drawer with j/k (and ←/→) wired to prev/next, which means a
 * reviewer never has to close it to advance.
 *
 * WHAT THE REBUILD FIXED
 * ----------------------
 * The old overlay was a hand-rolled `fixed inset-0` div: Escape did nothing,
 * Tab walked out of the dialog into the page behind the scrim, the body kept
 * scrolling underneath, clicking the backdrop did nothing, and focus was never
 * returned to the row you opened. All five now come from `<Drawer>`.
 *
 * It also rendered the analyser's internal failure string ("AI processing
 * failed.") to customers as if it were a summary, and printed a "Sentiment:
 * Unknown" card for calls that were never analysed at all. Absence of analysis
 * is now stated as absence, with the time the analysis was recorded, because
 * "we did not look" and "we looked and there was nothing" lead to different
 * actions.
 */

/* ─── Reading a call row ─────────────────────────────────────────────────── */

/**
 * Strings the pipeline has historically written into `summary` on failure. They
 * are internal diagnostics, never customer-facing copy, so they are read as an
 * absence of analysis rather than rendered.
 */
const FAILURE_TEXT = new Set([
  'ai processing failed.',
  'ai processing failed',
  'analysis failed',
  'processing failed',
  'n/a',
  'none',
  'null',
]);

/** Trim, then discard the failure sentinels. Returns '' for anything unusable. */
const readText = (value) => {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text) return '';
  return FAILURE_TEXT.has(text.toLowerCase()) ? '' : text;
};

/**
 * `duration` is a TEXT column holding seconds, so it arrives as '184' — and as
 * '' for a call whose duration never came through. Zero is a real duration (a
 * dropped connection); empty is not, and must stay unknown rather than becoming
 * a confident "0s".
 */
const toSeconds = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Stable identity for "the rubric has not loaded yet". */
const NO_CONFIGS = [];

/** Keys that are rendered by a dedicated part of this drawer, not the field list. */
const PROMOTED_KEYS = new Set([
  'category',
  'summary',
  'call_to_action',
  'quality_score',
  'quality_max',
  'quality_fields_counted',
  // AI Insights bookkeeping: scorecard totals render as their own blocks,
  // theme item tags only feed the dashboard
  '_scorecards',
  '_themes',
  // Why the worker has no analysis for this call; for retries, not the reader
  '_not_analysed',
]);

/** A custom-analysis value as one readable line. Never "[object Object]". */
const insightText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(insightText).filter(Boolean).join('; ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => {
        const text = insightText(v);
        return text ? `${k}: ${text}` : '';
      })
      .filter(Boolean)
      .join(' · ');
  }
  return '';
};

/**
 * Match a stored key to its insight config. `field_key` is the stable key; the
 * case-insensitive name match is the fallback for calls analysed before the
 * field_key migration, whose custom_analysis is keyed by display name.
 */
const findConfig = (configs, key) =>
  configs.find((c) => c.field_key === key) ||
  configs.find((c) => (c.field_name || '').toLowerCase() === String(key).toLowerCase()) ||
  null;

/**
 * When the analysis was recorded.
 *
 * `updated_at` is deliberately NOT consulted. A database trigger moves it on
 * every write to the row, including a reviewer flipping the follow-up status in
 * this very drawer — so using it would make the provenance line claim the call
 * had just been re-analysed. `created_at` is the insert, and the provider
 * pipeline inserts the analysis with the row, so it is the honest answer for
 * almost every call; an explicit timestamp from the pipeline wins when present.
 */
const analysisInstant = (call) => {
  const meta = (call && call.metadata) || {};
  const analysis = (call && call.custom_analysis) || {};
  const candidates = [
    analysis.analysed_at,
    analysis.analyzed_at,
    meta.analysed_at,
    meta.analyzed_at,
    call && call.created_at,
  ];
  return candidates.find((v) => typeof v === 'string' && v.trim()) || null;
};

/* ─── Transcript ─────────────────────────────────────────────────────────── */

// "Agent: we can book you in on Thursday" — a short label, then a colon. The
// length cap is what stops a sentence containing a colon ("One thing: ...") from
// being promoted to a speaker turn.
const SPEAKER_LINE = /^([A-Za-z][A-Za-z0-9 ._'-]{0,23}):\s*(.+)$/;

/**
 * Split a transcript into turns, but only claim speakers when the data really
 * distinguishes them: at least two distinct labels, on at least half the lines.
 * Inventing "Agent"/"Customer" over an unlabelled transcript would put words in
 * people's mouths, so an unlabelled one stays a sequence of plain lines.
 *
 * @returns {{ speakers: boolean, turns: Array<{speaker: string|null, text: string}> }}
 */
const parseTranscript = (raw) => {
  const lines = String(raw || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const turns = lines.map((line) => {
    const m = SPEAKER_LINE.exec(line);
    return m ? { speaker: m[1].trim(), text: m[2].trim() } : { speaker: null, text: line };
  });

  const labelled = turns.filter((t) => t.speaker);
  const distinct = new Set(labelled.map((t) => t.speaker.toLowerCase()));
  const speakers = distinct.size >= 2 && labelled.length >= lines.length / 2;

  return {
    speakers,
    turns: speakers ? turns : lines.map((text) => ({ speaker: null, text })),
  };
};

/** Wrap every occurrence of `needle` in a <mark>. Case-insensitive, no regex. */
const highlight = (text, needle) => {
  if (!needle) return text;
  const hay = text.toLowerCase();
  const pin = needle.toLowerCase();
  const out = [];
  let from = 0;
  let at = hay.indexOf(pin);
  while (at !== -1) {
    if (at > from) out.push(text.slice(from, at));
    out.push(
      // The default <mark> is a raw yellow with no known contrast against a
      // themed surface, so it is repainted in brand tint.
      <mark key={`${at}-${out.length}`} className="rounded-sm bg-brand-tint text-ink-900">
        {text.slice(at, at + pin.length)}
      </mark>,
    );
    from = at + pin.length;
    at = hay.indexOf(pin, from);
  }
  if (from < text.length) out.push(text.slice(from));
  return out;
};

const countMatches = (text, needle) => {
  if (!needle) return 0;
  const hay = text.toLowerCase();
  const pin = needle.toLowerCase();
  let n = 0;
  let at = hay.indexOf(pin);
  while (at !== -1) {
    n += 1;
    at = hay.indexOf(pin, at + pin.length);
  }
  return n;
};

/* ─── Follow-up status ───────────────────────────────────────────────────── */

/**
 * "pending" is labelled "Open": a follow-up nobody has picked up is open work,
 * whereas "pending" reads like something the system is doing on your behalf.
 *
 * The badge and the control carry different glyphs on purpose — a warning
 * triangle belongs on a status readout, not on a button you are choosing.
 */
const STATUS_META = {
  pending: { label: 'Open', tone: 'warn', icon: 'fa-circle-exclamation', choice: 'fa-circle-dot' },
  in_progress: {
    label: 'In progress',
    tone: 'brand',
    icon: 'fa-hourglass-half',
    choice: 'fa-hourglass-half',
  },
  done: { label: 'Done', tone: 'good', icon: 'fa-check', choice: 'fa-check' },
};

// Derived from the hook's own allowlist, so a fourth status added server-side
// appears here instead of silently becoming unreachable from this drawer.
const STATUS_OPTIONS = ACTION_STATUSES.map((value) => ({
  value,
  label: (STATUS_META[value] || {}).label || value,
  icon: (STATUS_META[value] || {}).choice,
}));

/**
 * Whether this call has audio we can actually serve, from the row alone.
 *
 * Only `gcs_audio_path` is proof: the signed-URL endpoint reads that column and
 * nothing else. `recUrl` is the provider's own link and is frequently present on
 * calls that were never copied into our bucket, so trusting it would put a play
 * button on calls that answer 404. Unknown returns null, which makes the player
 * ask the API instead of guessing.
 */
const knownRecording = (call) => {
  if (!call) return null;
  if (typeof call.gcs_audio_path === 'string' && call.gcs_audio_path.trim()) return true;
  if (call.recorded === false) return false;
  return null;
};

/* ─── Small presentational pieces ────────────────────────────────────────── */

/** One label/value pair in the header grid. */
function Meta({ label, value, mono = false }) {
  return (
    <div className="min-w-0">
      <dt className="text-micro text-ink-500">{label}</dt>
      <dd className={`truncate text-label text-ink-900 ${mono ? 'font-mono tabular-nums' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

/** One insight value; bullet-point insights are saved one point per line. */
function InsightValue({ field }) {
  const lines = field.bullets ? field.text.split('\n').map((l) => l.trim()).filter(Boolean) : null;
  return (
    <>
      {lines && lines.length > 1 ? (
        <ul className="list-disc space-y-0.5 pl-5 marker:text-ink-400">
          {lines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      ) : (
        field.text
      )}
      {field.max ? (
        <span className="font-mono text-micro tabular-nums text-ink-500">
          {' '}
          / {fmtInt(field.max)}
        </span>
      ) : null}
    </>
  );
}

function InsightList({ fields, className = 'mt-card' }) {
  if (!fields.length) return null;
  return (
    <dl className={`${className} divide-y divide-line border-t border-line`}>
      {fields.map((field) => (
        <div key={field.key} className="flex flex-wrap items-baseline gap-x-gutter gap-y-1 py-2">
          <dt className="min-w-[10rem] flex-1 basis-[10rem] text-label text-ink-500">{field.label}</dt>
          <dd className="min-w-0 flex-[2] basis-[14rem] text-body text-ink-900">
            <InsightValue field={field} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ title, subtitle, meta, actions, children }) {
  return (
    <Card as="section" className="mb-block">
      <CardHeader title={title} subtitle={subtitle} meta={meta} actions={actions} />
      {children}
    </Card>
  );
}

/* ─── The drawer ─────────────────────────────────────────────────────────── */

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {object|null} props.callData  a row from /calls
 * @param {Function} props.onClose
 * @param {Function} [props.onPrev]  previous call in the reviewer's queue
 * @param {Function} [props.onNext]  next call in the queue
 * @param {boolean} [props.hasPrev]  defaults to "onPrev was supplied"
 * @param {boolean} [props.hasNext]
 * @param {{index: number, total: number}} [props.position]  "3 of 25" in the footer
 */
const CallDetailsModal = ({
  isOpen,
  callData,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  position,
}) => {
  const { timeZone, hour12, targets, targetSource } = usePreferences();
  const updateStatus = useUpdateActionStatus();
  // Several screens render this drawer, and two of them portal it themselves, so
  // no id in here may be a literal.
  const uid = useId();
  const permalinkFieldId = `${uid}-permalink`;

  const call = callData || null;
  const open = Boolean(isOpen && call);
  const callId = call ? call.callId || call.id || null : null;

  const [transcriptQuery, setTranscriptQuery] = useState('');
  const [statusDraft, setStatusDraft] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [copyState, setCopyState] = useState('idle');
  const [exporting, setExporting] = useState(false);

  // Everything the reviewer typed or optimistically changed belongs to the call
  // they were looking at. Advancing the queue must not carry it forward.
  useEffect(() => {
    setTranscriptQuery('');
    setStatusDraft(null);
    setStatusError(null);
    setCopyState('idle');
  }, [callId]);

  /* The tenant's insight rubric: it supplies the display names, the field order,
     and the per-field maxima the quality score is graded against. */
  const configsQuery = useQuery({
    queryKey: ['custom-analysis-settings'],
    queryFn: async () => {
      const res = await authenticatedFetch(`${API_BASE}/api/custom-analysis-settings`);
      if (!res.ok) throw new Error('Could not load the insight configuration');
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: Boolean(isOpen),
    staleTime: 5 * 60 * 1000,
  });

  // A shared empty array, not a fresh `[]`: the memos below key off this
  // identity, and a new array every render would recompute all of them.
  const configs = configsQuery.data || NO_CONFIGS;

  /* The AI Insights setup: the top layer (it may be the system Call Category,
     which the rubric endpoint above leaves out), scorecards and groups. */
  const structureQuery = useQuery({
    queryKey: ['ai-insight-structure'],
    queryFn: async () => {
      const [cfgRes, grpRes] = await Promise.all([
        authenticatedFetch(`${API_BASE}/api/ai-insights`),
        authenticatedFetch(`${API_BASE}/api/ai-insight-groups`),
      ]);
      const all = cfgRes.ok ? await cfgRes.json() : [];
      const grp = grpRes.ok ? await grpRes.json() : [];
      return { all: Array.isArray(all) ? all : [], groups: Array.isArray(grp) ? grp : [] };
    },
    enabled: Boolean(isOpen),
    staleTime: 5 * 60 * 1000,
  });
  const allConfigs = (structureQuery.data && structureQuery.data.all) || NO_CONFIGS;
  const groups = (structureQuery.data && structureQuery.data.groups) || NO_CONFIGS;
  const topLayer = useMemo(
    () => allConfigs.find((c) => c.is_top_layer && c.is_active !== false) || null,
    [allConfigs],
  );

  /* ─── Derived call facts ─────────────────────────────────────────────── */

  const analysis = (call && call.custom_analysis) || {};
  const summary = readText(call && call.summary) || readText(analysis.summary);
  const action = readText(call && call.call_to_action) || readText(analysis.call_to_action);
  const category = readText(call && call.category) || readText(analysis.category);
  const sentiment = readText(call && call.sentiment);
  const transcript = readText(call && call.transcription);

  // The top-layer answer decides which insights ran on this call
  const topValue = topLayer
    ? readText(analysis[topLayer.field_key]) || (topLayer.field_key === 'category' ? category : '')
    : '';

  const qualityScore = toNumber(call && call.quality_score) ?? toNumber(analysis.quality_score);

  // The rubric maximum stored WITH the call wins: a call scored 18/20 last month
  // must keep reading as 90% after the tenant grows the rubric to 30 points.
  const rubricMax = useMemo(
    () =>
      configs
        .filter((c) => c.field_type === 'scoring' && c.is_active !== false && !c.is_system)
        .reduce((sum, c) => sum + (toNumber(c.max_score) || 0), 0),
    [configs],
  );
  const qualityMax = toNumber(analysis.quality_max) ?? (rubricMax > 0 ? rubricMax : null);
  const attainment = ratio(qualityScore, qualityMax);

  // No target configured means no verdict. `|| 0` here would have declared every
  // score on earth a pass, which is the exact defaulting this rebuild exists to
  // remove — so a missing target renders the attainment with no tone at all.
  const qualityTarget = toNumber(targets && targets.quality_attainment);
  const meetsTarget =
    attainment === null || qualityTarget === null ? null : attainment >= qualityTarget;

  const scoredFields = toNumber(analysis.quality_fields_counted);
  const rubricFields = useMemo(
    () =>
      configs.filter((c) => c.field_type === 'scoring' && c.is_active !== false && !c.is_system)
        .length,
    [configs],
  );

  /** Custom insight fields, in rubric order, unrecognised keys last. */
  const insightFields = useMemo(() => {
    const stored = (call && call.custom_analysis) || {};
    const keys = Object.keys(stored).filter(
      (k) =>
        !PROMOTED_KEYS.has(k) &&
        // the top layer has its own banner
        !(topLayer && k === topLayer.field_key) &&
        insightText(stored[k]),
    );
    const ordered = [
      ...configs.map((c) => c.field_key).filter((k) => k && keys.includes(k)),
      // Keys the rubric no longer describes still get shown, under their raw
      // name: a field the owner deleted last week is still what the call was
      // judged on, and dropping it would make the record incomplete.
      ...keys.filter((k) => !configs.some((c) => c.field_key === k)),
    ];
    return ordered.map((key) => {
      const config = findConfig(configs, key);
      const meta = findConfig(allConfigs, key) || config;
      const max = config ? toNumber(config.max_score) : null;
      return {
        key,
        label: (config && config.field_name) || key,
        text: insightText(stored[key]),
        // A scoring field is meaningless without its denominator: "7" could be
        // excellent or a disaster depending on whether the item is out of 7 or 20.
        max: config && config.field_type === 'scoring' ? max : null,
        type: meta ? meta.field_type : null,
        groupId: meta ? meta.group_id : null,
        bullets: Boolean(meta && meta.output_format === 'bullets'),
      };
    });
  }, [call, configs, allConfigs, topLayer]);

  /* Scorecards saved on the call (one block each, with their parameters),
     classification groups, and what the rest of the list keeps. */
  const layout = useMemo(() => {
    const byId = Object.fromEntries(groups.map((g) => [g.id, g]));
    const stored = (call && call.custom_analysis) || {};
    const saved = stored._scorecards && typeof stored._scorecards === 'object' ? stored._scorecards : {};
    const cards = Object.entries(saved).map(([key, card]) => ({
      key,
      name: (card && card.name) || key,
      score: toNumber(card && card.score),
      max: toNumber(card && card.max),
      pct: toNumber(card && card.pct),
      items: insightFields.filter((f) => f.type === 'scoring' && byId[f.groupId] && byId[f.groupId].key === key),
    }));
    const classGroups = groups
      .filter((g) => g.group_type === 'classification')
      .map((g) => ({ ...g, items: insightFields.filter((f) => f.type === 'categorization' && f.groupId === g.id) }))
      .filter((g) => g.items.length > 0);
    const placed = new Set([...cards, ...classGroups].flatMap((b) => b.items.map((i) => i.key)));
    const notRun = topLayer && topValue
      ? allConfigs.filter(
          (c) =>
            !c.is_system &&
            !c.is_top_layer &&
            c.is_active !== false &&
            (c.applies_to || []).length > 0 &&
            !(c.applies_to || []).some((a) => String(a).toLowerCase() === topValue.toLowerCase()),
        )
      : [];
    return { cards, classGroups, rest: insightFields.filter((f) => !placed.has(f.key)), notRun };
  }, [call, groups, insightFields, allConfigs, topLayer, topValue]);

  const analysedAt = analysisInstant(call);
  const analysisFailed =
    Boolean(call && call.metadata && call.metadata.analysis_status === 'failed');
  const analysed =
    !analysisFailed &&
    Boolean(summary || sentiment || qualityScore !== null || insightFields.length > 0);

  // The server treats anything unset as pending, so this must too — otherwise
  // the control would show nothing selected for the majority of calls.
  const status = statusDraft || normaliseActionStatus(call && call.action_status);
  const statusMeta = STATUS_META[status] || STATUS_META.pending;

  const durationSeconds = toSeconds(call && call.duration);
  // Prefer the lead's own name over this call's phonebookName: the lead record
  // is the one a human curates, the phonebook name is whatever the provider sent.
  const customerName =
    (call && (readText(call.lead_name) || readText(call.phonebookName))) || '';
  const phone = (call && (call.formattedNumber || call.number)) || '';
  const agent = (call && readText(call.userName)) || '';
  const when = call && call.startTime
    ? formatInstant(call.startTime, timeZone, { withYear: true, hour12 })
    : '';

  const transcriptParsed = useMemo(() => parseTranscript(transcript), [transcript]);
  const matchingTurns = useMemo(() => {
    const needle = transcriptQuery.trim();
    if (!needle) return transcriptParsed.turns;
    return transcriptParsed.turns.filter(
      (t) =>
        t.text.toLowerCase().includes(needle.toLowerCase()) ||
        (t.speaker || '').toLowerCase().includes(needle.toLowerCase()),
    );
  }, [transcriptParsed, transcriptQuery]);
  const matchCount = useMemo(() => {
    const needle = transcriptQuery.trim();
    if (!needle) return 0;
    return transcriptParsed.turns.reduce((n, t) => n + countMatches(t.text, needle), 0);
  }, [transcriptParsed, transcriptQuery]);

  /* ─── Queue navigation ───────────────────────────────────────────────── */

  const canPrev = Boolean(onPrev) && hasPrev !== false;
  const canNext = Boolean(onNext) && hasNext !== false;

  // Read through a ref so the key listener is bound once per open rather than
  // being torn down and re-added every time the parent re-renders — which, on a
  // list screen, is every time the query refetches.
  const navRef = useRef({ onPrev, onNext, canPrev, canNext });
  useEffect(() => {
    navRef.current = { onPrev, onNext, canPrev, canNext };
  }, [onPrev, onNext, canPrev, canNext]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      // A control inside the drawer that already consumed the key keeps it: the
      // segmented control's arrows move its selection, and the audio region's
      // arrows scrub. Both call preventDefault.
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (target) {
        if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
        if (target.isContentEditable) return;
      }

      const { onPrev: prev, onNext: next, canPrev: allowPrev, canNext: allowNext } = navRef.current;
      // ←/→ rather than ↑/↓: vertical arrows scroll the transcript, and a
      // reviewer mid-transcript pressing Down expects to keep reading.
      const wantsNext = event.key === 'j' || event.key === 'ArrowRight';
      const wantsPrev = event.key === 'k' || event.key === 'ArrowLeft';

      if (wantsNext && allowNext && next) {
        event.preventDefault();
        next();
      } else if (wantsPrev && allowPrev && prev) {
        event.preventDefault();
        prev();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  /* ─── Permalink ──────────────────────────────────────────────────────── */

  // One canonical address per call, independent of which screen the drawer was
  // opened from, so a link pasted into a chat opens the same call for everyone.
  const permalink = callId
    ? `${window.location.origin}/calls?call=${encodeURIComponent(callId)}`
    : null;

  const copyPermalink = useCallback(async () => {
    if (!permalink) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(permalink);
        setCopyState('copied');
        return;
      }
      throw new Error('clipboard unavailable');
    } catch {
      // The Clipboard API needs a secure context and a permission the browser
      // can refuse. Rather than failing silently, show the link to copy by hand.
      setCopyState('failed');
    }
  }, [permalink]);

  useEffect(() => {
    if (copyState !== 'copied') return undefined;
    const timer = setTimeout(() => setCopyState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  /* ─── Follow-up status ───────────────────────────────────────────────── */

  const changeStatus = useCallback(
    (next) => {
      if (!callId || next === status) return;
      // Optimistic, because the control has to feel like a control. The draft is
      // dropped again if the write fails, so the badge can never disagree with
      // the server for longer than the round trip.
      setStatusDraft(next);
      setStatusError(null);
      // The hook already invalidates every reader of action_status, the calls
      // list included, so there is nothing to refresh from here.
      updateStatus.mutate(
        { callId, actionStatus: next },
        {
          onError: (error) => {
            setStatusDraft(null);
            // The hook throws with the server's own sentence where it gave one,
            // and "Call not found or access denied" is worth showing verbatim.
            setStatusError((error && error.message) || 'The status did not save. Try again.');
          },
        },
      );
    },
    [callId, status, updateStatus],
  );

  /* ─── Export ─────────────────────────────────────────────────────────── */

  const exportDetails = useCallback(async () => {
    if (!call) return;
    setExporting(true);
    try {
      let recordingUrl = 'Not available';
      if (callId) {
        try {
          const res = await authenticatedFetch(
            `${API_BASE}/api/audio/signed-url/${encodeURIComponent(callId)}?expiration_minutes=120`,
          );
          const data = res.ok ? await res.json() : null;
          if (data && data.signed_url) recordingUrl = data.signed_url;
        } catch {
          recordingUrl = 'Not available';
        }
      }

      // 'Unknown' rather than the em dash the UI uses: a text file is read
      // without the surrounding column, and a bare dash there is ambiguous.
      const line = (label, value) => `${label}: ${value || 'Unknown'}`;
      const body = [
        'CallVibe - call detail',
        '',
        line('Call ID', callId),
        line('Customer', customerName),
        line('Number', phone),
        line('Agent', agent),
        line('When', when),
        line('Duration', durationSeconds === null ? null : fmtDurationWords(durationSeconds)),
        line(
          'Direction',
          call.inbound === null || call.inbound === undefined
            ? null
            : call.inbound === true || call.inbound === 'true'
              ? 'Inbound'
              : 'Outbound',
        ),
        line('Category', category),
        line('Recording', recordingUrl),
        '',
        'ANALYSIS',
        analysed
          ? [
              line('Analysis recorded', analysedAt ? formatInstant(analysedAt, timeZone, { withYear: true, hour12 }) : 'not recorded'),
              line('Sentiment', sentiment),
              line(
                'Quality',
                qualityScore === null
                  ? null
                  : `${fmtInt(qualityScore)}${qualityMax ? ` / ${fmtInt(qualityMax)}` : ''}${
                      attainment === null ? '' : ` (${fmtPercent(attainment)})`
                    }`,
              ),
              topLayer && topValue ? line(topLayer.field_name, topValue) : null,
              ...layout.cards.map((c) =>
                line(c.name, c.score === null ? null : `${fmtInt(c.score)} / ${fmtInt(c.max)}${c.pct === null ? '' : ` (${fmtInt(c.pct)}%)`}`),
              ),
              '',
              'Summary',
              summary || 'No summary was produced.',
              '',
              ...insightFields.map((f) => line(f.label, f.max ? `${f.text} / ${f.max}` : f.text)),
            ].join('\n')
          // The export must not invent an analysis either — it is the same data,
          // and it is the version that gets forwarded outside the company.
          : 'This call was not analysed, so there is no summary, sentiment or score for it.',
        '',
        'FOLLOW-UP',
        line('Status', statusMeta.label),
        action || 'No follow-up was identified on this call.',
        '',
        'TRANSCRIPT',
        transcript || 'No transcript was stored for this call.',
        '',
        `Exported ${formatInstant(new Date().toISOString(), timeZone, { withYear: true, hour12 })}`,
        '',
      ].join('\n');

      const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
      const href = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = `call-${callId || 'detail'}.txt`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(href);
    } finally {
      setExporting(false);
    }
  }, [
    call,
    callId,
    customerName,
    phone,
    agent,
    when,
    durationSeconds,
    category,
    analysed,
    analysedAt,
    sentiment,
    qualityScore,
    qualityMax,
    attainment,
    summary,
    insightFields,
    layout,
    topLayer,
    topValue,
    statusMeta,
    action,
    transcript,
    timeZone,
    hour12,
  ]);

  /* ─── Render ─────────────────────────────────────────────────────────── */

  const footer = (
    <>
      <div className="mr-auto flex items-center gap-2">
        <IconButton
          icon="fa-chevron-left"
          label="Previous call - k or left arrow"
          onClick={onPrev}
          disabled={!canPrev}
        />
        <IconButton
          icon="fa-chevron-right"
          label="Next call - j or right arrow"
          onClick={onNext}
          disabled={!canNext}
        />
        {position && Number.isFinite(position.index) && Number.isFinite(position.total) ? (
          <span className="font-mono text-micro tabular-nums text-ink-500">
            {fmtInt(position.index)} / {fmtInt(position.total)}
          </span>
        ) : (canPrev || canNext) ? (
          <span className="text-micro text-ink-500">j / k to move</span>
        ) : null}
      </div>
      <Button iconLeft="fa-download" onClick={exportDetails} loading={exporting}>
        Export
      </Button>
      <Button variant="primary" onClick={onClose}>
        Close
      </Button>
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="xl"
      title={customerName || (phone ? fmtPhone(phone) : 'Call detail')}
      subtitle={customerName && phone ? fmtPhone(phone) : undefined}
      footer={footer}
    >
      {call ? (
        <>
          {/* Identity and circumstances — everything needed to know which call
              this is without scrolling. */}
          <Card as="section" className="mb-block">
            <div className="flex flex-wrap items-start justify-between gap-gutter">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <DirectionBadge inbound={call.inbound} />
                <OutcomeBadge answered={call.answered} inbound={call.inbound} />
                {category ? <Badge icon="fa-tag">{category}</Badge> : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {copyState === 'copied' ? (
                  <span className="text-micro text-good-text" role="status">
                    Link copied
                  </span>
                ) : null}
                <IconButton
                  icon="fa-link"
                  label="Copy a link to this call"
                  onClick={copyPermalink}
                  disabled={!permalink}
                />
              </div>
            </div>

            {copyState === 'failed' && permalink ? (
              <div className="mt-gutter">
                <label className="text-micro text-ink-500" htmlFor={permalinkFieldId}>
                  Your browser blocked the clipboard - copy this link by hand
                </label>
                <input
                  id={permalinkFieldId}
                  type="text"
                  readOnly
                  value={permalink}
                  onFocus={(event) => event.target.select()}
                  className="mt-1 h-[34px] w-full rounded-md border border-line bg-surface-2 px-2 font-mono text-micro text-ink-900"
                />
              </div>
            ) : null}

            <dl className="mt-card grid grid-cols-2 gap-x-gutter gap-y-gutter sm:grid-cols-3">
              <Meta label="Agent" value={agent || EMPTY} />
              <Meta label="When" value={when || EMPTY} />
              <Meta
                label="Duration"
                mono
                value={durationSeconds === null ? EMPTY : fmtDurationWords(durationSeconds)}
              />
              <Meta label="Channel" value={readText(call.sourceDetail) || EMPTY} />
              <Meta label="Number" mono value={phone ? fmtPhone(phone) : EMPTY} />
              <Meta label="Call ID" mono value={callId || EMPTY} />
            </dl>
          </Card>

          {/* The recording. `initialHasAudio` short-circuits the availability
              probe using what the row already told us. */}
          <div className="mb-block">
            <AudioPlayer callId={callId} initialHasAudio={knownRecording(call)} />
          </div>

          {/* What the analyser found — or the fact that it never ran. */}
          <Section
            title="AI analysis"
            subtitle="What the analyser heard on this call."
            meta={
              // Provenance sits in the header, not buried at the bottom: whether
              // this reading is an hour old or four months old changes how much
              // weight it deserves.
              analysed
                ? analysedAt
                  ? `analysed ${formatInstant(analysedAt, timeZone, { hour12 })}`
                  : 'analysis time not recorded'
                : undefined
            }
          >
            {analysed ? (
              <>
                {/* Top layer — decides which insights ran on this call */}
                {topLayer && topValue ? (
                  <div className="mb-card flex flex-col gap-2 rounded-lg border border-brand-line bg-brand-tint px-card py-2.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-layer-group shrink-0 text-brand" aria-hidden="true" />
                      <span className="text-label text-ink-700">{topLayer.field_name}</span>
                      <span className="text-body font-semibold text-ink-900">{topValue}</span>
                    </div>
                    <p className="text-micro text-ink-500 sm:text-right">
                      Ran the insights set to {topValue}, plus the ones set to every call.
                    </p>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-gutter">
                  <SentimentBadge sentiment={sentiment} />
                  {qualityScore !== null ? (
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono text-title tabular-nums text-ink-900">
                        {fmtInt(qualityScore)}
                        {qualityMax ? (
                          <span className="text-body text-ink-500">/{fmtInt(qualityMax)}</span>
                        ) : null}
                      </span>
                      {attainment !== null ? (
                        <Badge
                          // Two states, not five bands: the scorecard either met
                          // the tenant's target or it did not. Amber rather than
                          // red because one call below target is not a failure.
                          tone={meetsTarget === null ? 'neutral' : meetsTarget ? 'good' : 'warn'}
                          icon={
                            meetsTarget === null
                              ? 'fa-percent'
                              : meetsTarget
                                ? 'fa-check'
                                : 'fa-arrow-down'
                          }
                        >
                          {fmtPercent(attainment)} attainment
                        </Badge>
                      ) : null}
                      <Target
                        value={qualityTarget}
                        format="percent"
                        source={targetSource && targetSource.quality_attainment}
                      />
                    </div>
                  ) : null}
                </div>

                {qualityScore !== null && scoredFields !== null && rubricFields > 0 ? (
                  <p className="mt-2 text-micro text-ink-500">
                    {/* Coverage of the rubric, because a score out of the items
                        the model actually answered is not a score out of the
                        whole scorecard. */}
                    {fmtInt(scoredFields)} of {fmtInt(rubricFields)} scorecard items were answered
                  </p>
                ) : null}

                <div className="mt-card">
                  <h4 className="text-label font-medium text-ink-700">Summary</h4>
                  {summary ? (
                    <p className="mt-1 max-w-[68ch] text-body text-ink-900">{summary}</p>
                  ) : (
                    <p className="mt-1 text-body text-ink-500">
                      No summary was produced for this call.
                    </p>
                  )}
                </div>

                {configsQuery.isPending ? (
                  <div className="mt-card flex items-center gap-2 text-label text-ink-500">
                    <Spinner size={12} />
                    Loading the insight labels
                  </div>
                ) : null}

                {/* One block per scorecard saved on the call */}
                {layout.cards.map((card) => (
                  <div key={card.key} className="mt-card rounded-lg border border-line">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 px-card py-2.5">
                      <h4 className="text-label font-semibold text-ink-900">
                        {card.name}
                        <span className="ml-1.5 font-normal text-ink-500">
                          · {card.items.length} parameter{card.items.length === 1 ? '' : 's'}
                        </span>
                      </h4>
                      {card.score !== null ? (
                        <span className="flex items-baseline gap-2">
                          <span className="font-mono text-body tabular-nums text-ink-900">
                            {fmtInt(card.score)}
                            <span className="text-micro text-ink-500">/{fmtInt(card.max)}</span>
                          </span>
                          {card.pct !== null ? <Badge tone="neutral" size="sm">{fmtInt(card.pct)}%</Badge> : null}
                        </span>
                      ) : null}
                    </div>
                    <div className="px-card">
                      <InsightList fields={card.items} className="" />
                    </div>
                  </div>
                ))}

                {/* Classification groups: related dropdowns together */}
                {layout.classGroups.map((g) => (
                  <div key={g.id} className="mt-card">
                    <h4 className="text-micro font-semibold uppercase tracking-wide text-ink-500">{g.name}</h4>
                    <InsightList fields={g.items} className="mt-1" />
                  </div>
                ))}

                {layout.rest.length > 0 && (layout.cards.length > 0 || layout.classGroups.length > 0) ? (
                  <h4 className="mt-card text-micro font-semibold uppercase tracking-wide text-ink-500">Other insights</h4>
                ) : null}
                <InsightList
                  fields={layout.rest}
                  className={layout.cards.length > 0 || layout.classGroups.length > 0 ? 'mt-1' : 'mt-card'}
                />

                {layout.notRun.length > 0 ? (
                  <p className="mt-card text-micro text-ink-500">
                    <span className="font-medium text-ink-700">Not run on this call:</span>{' '}
                    {layout.notRun
                      .map((c) => `${c.field_name} (${(c.applies_to || []).join(', ')} only)`)
                      .join(' · ')}
                  </p>
                ) : null}
              </>
            ) : (
              <EmptyState
                compact
                variant="no-data"
                icon="fa-microphone-slash"
                title={analysisFailed ? 'Analysis failed for this call' : 'This call was not analysed'}
                body={
                  analysisFailed
                    ? 'The analyser ran and did not complete, so no summary, sentiment or score was stored. Nothing here is inferred from the partial result.'
                    : 'No summary, sentiment or score was stored for this call. Analysis runs on the recording, so a call with no recording never reaches the analyser.'
                }
              />
            )}
          </Section>

          {/* Follow-up: the one thing in this drawer a reviewer can change. */}
          <Section
            title="Follow-up"
            subtitle="What this call asked someone to do next."
            actions={
              // No badge when there is nothing to follow up: a call with no
              // action item is not "Open work", it is simply finished.
              action ? (
                <Badge tone={statusMeta.tone} icon={statusMeta.icon}>
                  {statusMeta.label}
                </Badge>
              ) : null
            }
          >
            {action ? (
              <p className="max-w-[68ch] text-body text-ink-900">{action}</p>
            ) : (
              <p className="text-body text-ink-500">No follow-up was identified on this call.</p>
            )}

            {action ? (
              <div className="mt-card flex flex-wrap items-center gap-gutter">
                <SegmentedControl
                  value={status}
                  onChange={changeStatus}
                  options={STATUS_OPTIONS}
                  label="Follow-up status"
                />
                {updateStatus.isPending ? <Spinner size={14} className="text-ink-500" /> : null}
                {statusError ? (
                  <span className="text-label text-bad-text" role="alert">
                    {statusError}
                  </span>
                ) : null}
              </div>
            ) : null}
          </Section>

          {/* Transcript last: it is the longest thing here and the least often
              read end to end, which is what the search box is for. */}
          <Section
            title="Transcript"
            subtitle={
              transcriptParsed.speakers
                ? 'Speaker turns as the transcription service labelled them.'
                : 'As transcribed, one line per sentence.'
            }
            meta={
              transcript
                ? `${fmtInt(transcriptParsed.turns.length)} ${
                    transcriptParsed.speakers ? 'turns' : 'lines'
                  }`
                : undefined
            }
          >
            {transcript ? (
              <>
                <SearchInput
                  value={transcriptQuery}
                  onChange={setTranscriptQuery}
                  placeholder="Search this transcript"
                  className="max-w-sm"
                />

                {transcriptQuery.trim() ? (
                  <p className="mt-2 text-micro text-ink-500" role="status">
                    {matchCount > 0
                      ? `${fmtInt(matchCount)} ${matchCount === 1 ? 'match' : 'matches'} in ${fmtInt(
                          matchingTurns.length,
                        )} of ${fmtInt(transcriptParsed.turns.length)} lines`
                      : 'No line in this transcript contains that.'}
                  </p>
                ) : null}

                {/* 68ch is the readable measure. The transcript scrolls inside
                    its own box so the drawer's own scroll position — and the
                    header above it — stay put while reading. */}
                <div className="thin-scrollbar mt-gutter max-h-[28rem] overflow-y-auto rounded-md border border-line bg-surface-2 p-gutter">
                  <div className="max-w-[68ch] space-y-3">
                    {matchingTurns.map((turn, i) => (
                      <p key={`${i}-${turn.text.slice(0, 24)}`} className="text-body text-ink-900">
                        {turn.speaker ? (
                          <span className="mb-0.5 block text-micro font-medium uppercase tracking-wide text-ink-500">
                            {turn.speaker}
                          </span>
                        ) : null}
                        {highlight(turn.text, transcriptQuery.trim())}
                      </p>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <EmptyState
                compact
                variant="no-data"
                icon="fa-file-lines"
                title="No transcript was stored"
                body="Transcription runs on the recording, so a call with no recording has no transcript. If this one should have been recorded, check the recording settings for that line."
              />
            )}
          </Section>
        </>
      ) : null}
    </Drawer>
  );
};

export default CallDetailsModal;
